import React, { useState, useEffect, useRef } from 'react';
import { getConnectLabel } from 'utils/brand';
import classNames from 'classnames';
import css from './style.scss';
import { closePopup, getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import infoGray from 'assets/images/info-gray.svg';
import connectPromoIllustrationFull from 'assets/images/skip-trace-connect-promo/connect-promo-illustration-full.svg';
import connectPromoCloseIcon from 'assets/images/skip-trace-connect-promo/connect-promo-close-icon.svg';
import connectPromoGiftIcon from 'assets/images/skip-trace-connect-promo/connect-promo-gift-icon.svg';
import connectPromoCheckIcon from 'assets/images/skip-trace-connect-promo/connect-promo-check-icon.svg';
import connectPromoSavingsIcon from 'assets/images/skip-trace-connect-promo/connect-promo-savings-icon.svg';
import { useToast } from '../ToastMessage/ToastManager';
import Tooltip from '../../components/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCardList,
  selectListManagementEnabled,
  selectPrepaidCredits,
  selectUserId,
  selectPlanDetails,
  isLegacyAccountPlan,
  selectShouldPromotePropStreamConnect,
  selectIsLeadAutomatorLoaded,
  selectIsLeadAutomatorLoading,
  getLeadAutomatorAPI,
} from '../../../data/user';
import { appendContacts, getAppendJobEstimate, selectAppendJob } from '../../../data/contacts';
import { propertySkipTrace } from '../../../data/property';
import NewCreditCardDropdown from '../NewCreditCardDropdown';
import DNCPolicyModal from '../DNCPolicyModal';
import { isCardExpired } from '../../../utils/validation';
import { selectLoading } from '../../../data/contacts';
import eyeOpen from 'assets/images/eye-on.svg';
import eyeClosed from 'assets/images/eye-off.svg';
import SVG from 'components/base/SVG';
import InfoAlert from '../InfoAlert';
import AddPaymentPopup from '../../AccountNew/modals/AddPayment';
import CheckboxItem from '../../../components/Checkbox';
import { goToLeadAutomatorUpgrade } from '../../AccountNew/Sections/LeadAutomator';
import LeaveMessagePopup from '../../AccountNew/modals/LeaveMessage';

const BODY_CLASS_NAME = 'bodyModal';

const CONNECT_PROMO_STORAGE_PREFIX = 'psConnectPromoDismissedDate_';

const isConnectPromoSuppressedToday = (userId) => {
  if (!userId) return false;
  try {
    return (
      localStorage.getItem(`${CONNECT_PROMO_STORAGE_PREFIX}${userId}`) === new Date().toDateString()
    );
  } catch {
    return false;
  }
};

const suppressConnectPromoForToday = (userId) => {
  if (!userId) return;
  try {
    localStorage.setItem(`${CONNECT_PROMO_STORAGE_PREFIX}${userId}`, new Date().toDateString());
  } catch {
    // localStorage unavailable — suppression simply won't persist across sessions
  }
};

const SkipTraceModal = ({
  title = 'Skip Trace',
  groups: _groups = [],
  closePopup,
  reprocessEnabled,
  contactIds,
  selectedProperties,
  isPropertySkipTrace,
  onSuccess,
  onCancel,
  // Property skip trace only: called when the trace didn't complete, with the API's status
  // (NO_MATCH, DUPLICATE, VALIDATION_ERROR, PAYMENT_ERROR, ERROR) or 'ERROR' when the request failed.
  onFailure,
}) => {
  const modalRef = useRef(null);
  const showToast = useToast();
  const cardList = useSelector(selectCardList);
  const dispatch = useDispatch();
  const userId = useSelector(selectUserId);
  const planDetails = useSelector(selectPlanDetails)?.toJS();
  const isLegacyAccount = useSelector(isLegacyAccountPlan);
  const shouldPromotePropStreamConnect = useSelector(selectShouldPromotePropStreamConnect);
  const isLeadAutomatorLoaded = useSelector(selectIsLeadAutomatorLoaded);
  const isLeadAutomatorLoading = useSelector(selectIsLeadAutomatorLoading);
  const [connectPromoDismissed, setConnectPromoDismissed] = useState(() =>
    isConnectPromoSuppressedToday(userId)
  );
  const [dontShowConnectPromoAgainToday, setDontShowConnectPromoAgainToday] = useState(false);
  const showConnectPromo = shouldPromotePropStreamConnect && !connectPromoDismissed;

  // PROPS-3091: selectShouldPromotePropStreamConnect fails safe (won't promote) until
  // accountPlanMrcs has loaded, so make sure it actually gets fetched here rather than
  // depending on some other component having already loaded it earlier in the session.
  useEffect(() => {
    // Deliberately only depends on userId (matching LeadAutomator.js's own fetch effect) —
    // depending on isLeadAutomatorLoaded/isLeadAutomatorLoading here would re-run this
    // effect every time a failed fetch resets isLeadAutomatorLoading back to false,
    // causing an infinite retry loop against the API.
    if (userId && !isLeadAutomatorLoaded && !isLeadAutomatorLoading) {
      dispatch(getLeadAutomatorAPI(userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dispatch]);
  const [reSkipTrace, setReSkipTrace] = useState(reprocessEnabled || false);
  const [selectedCard, setSelectedCard] = useState(cardList[0] || {});
  const [cvv, setCvv] = useState('');
  const [cvvVisible, setCvvVisible] = useState(false);
  const [cvvError, setCvvError] = useState('');
  const [listName, setListName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const appendJobState = useSelector(selectAppendJob);
  const appendJob =
    appendJobState && typeof appendJobState.toJS === 'function'
      ? appendJobState.toJS()
      : appendJobState || {};
  // const appendJob = useSelector(state => selectAppendJob(state).toJS());
  const listManagementEnabled = useSelector(selectListManagementEnabled);
  const loading = useSelector(selectLoading);
  const prepaidInfoState = useSelector(selectPrepaidCredits);
  const prepaidInfo =
    prepaidInfoState && typeof prepaidInfoState.toJS === 'function'
      ? prepaidInfoState.toJS()
      : prepaidInfoState || {};

  const validateCVV = (value) => {
    const requiredLength = selectedCard?.cardCVVLength ? selectedCard?.cardCVVLength : 3;

    if (!value) {
      return 'CVV is required';
    }

    if (!/^\d+$/.test(value)) {
      return 'CVV must contain only numbers';
    }

    if (value.length !== requiredLength) {
      return `CVV must be ${requiredLength} digits`;
    }

    return '';
  };

  const handleCvvChange = (e) => {
    const value = e.target.value;

    // Allow only numbers
    if (!/^\d*$/.test(value)) return;

    setCvv(value);

    // Live validation
    setCvvError(validateCVV(value));
  };

  const handleCardChange = (selectedOption) => {
    setSelectedCard(selectedOption);
  };

  const handleClose = ({ cancelled } = {}) => {
    document.body.classList.remove(BODY_CLASS_NAME);
    if (cancelled) onCancel?.();
    closePopup?.();
  };

  const AddPaymentMethod = () => {
    dispatch(AddPaymentPopup.open());
  };

  const handleContinuePaidSkipTrace = () => {
    if (dontShowConnectPromoAgainToday) suppressConnectPromoForToday(userId);
    setConnectPromoDismissed(true);
  };

  const handleUpgradeToConnect = () => {
    if (dontShowConnectPromoAgainToday) suppressConnectPromoForToday(userId);
    handleClose();
    goToLeadAutomatorUpgrade({
      planDetails,
      isLegacyAccount,
      dispatch,
      // Outside the router tree, so use App's global window.pushRoute for client-side nav.
      history: {
        push: (path) => {
          window.pushRoute(path);
        },
      },
      leaveMessagePopup: (options) => dispatch(LeaveMessagePopup.open(options)),
    });
  };

  useEffect(() => {
    document.body.classList.add(BODY_CLASS_NAME);

    // Keyboard focus must stay trapped inside the modal while it's open — otherwise
    // Tab/Shift+Tab can walk focus into the grid/sidebar behind the overlay.
    const previouslyFocusedElement = document.activeElement;
    modalRef.current?.focus();

    const getFocusableElements = () => {
      if (!modalRef.current) return [];
      return Array.from(
        modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
    };

    const keydownHandler = (e) => {
      if (e.key === 'Escape') {
        handleClose({ cancelled: true });
        return;
      }

      if (e.key !== 'Tab' || !modalRef.current) return;

      const focusable = getFocusableElements();
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const isInsideModal = modalRef.current.contains(document.activeElement);

      if (e.shiftKey) {
        if (!isInsideModal || document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!isInsideModal || document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const clickOutside = () => {
      // TODO: Fix click outside issue with modal content with better approach
      // if (e.target.closest('.modal-content')) {
      //   return;
      // }
      // if (modalRef.current && !modalRef.current.contains(e.target)) {
      //   handleClose();
      // }
    };

    // Closes the popup on browser back, since it has no history entry of its own.
    const popstateHandler = () => handleClose({ cancelled: true });

    document.addEventListener('keydown', keydownHandler, true);
    document.addEventListener('mousedown', clickOutside, true);
    window.addEventListener('popstate', popstateHandler);

    return () => {
      document.removeEventListener('keydown', keydownHandler, true);
      document.removeEventListener('mousedown', clickOutside, true);
      window.removeEventListener('popstate', popstateHandler);
      document.body.classList.remove(BODY_CLASS_NAME);
      previouslyFocusedElement?.focus?.();
    };
  }, []);

  const openDNCPolicyModal = () => {
    dispatch(DNCPolicyModal.open());
  };

  const handlereSkipTraceChange = () => {
    setReSkipTrace((val) => {
      const mode = 'BOTH';
      dispatch(
        getAppendJobEstimate({
          contactIds,
          appendMode: mode,
          reprocessEnabled: !val,
        })
      );
      return !val;
    });
  };

  const handlePlaceOrder = () => {
    if (isSubmitting || !listName.trim()) return;
    if (appendJob?.newQuantity <= 0) {
      dispatch(
        InfoAlert.open({
          title: 'No Eligible Contacts Found',
          infoText:
            'Sorry, none of the selected contacts are eligible for Skip Trace. Please select different properties and try again.',
          closePopup: InfoAlert.close(),
        })
      );
      return;
    }

    setIsSubmitting(true);

    // 1. Collect payment data from selectedCard
    const paymentMethodId = selectedCard?.paymentMethodId;
    const cardCode = cvv ? cvv : null;

    const lines = appendJob.lines || [];
    const reprocessEnabled = reSkipTrace;

    // 3. Amounts from summary
    const paymentAmount = appendJob?.total || 0;
    const creditAmount = appendJob?.credits || 0;

    const calculatedRate = appendJob?.total === 0 ? 0 : appendJob?.rate;

    const { rate: _rate, ...appendJobWithoutRates } = appendJob;

    // 4. FINAL CALL (same as old modal)
    const payload = {
      ...appendJobWithoutRates,
      reprocessEnabled,
      lines,
      name: listName,
      rate: calculatedRate,
    };

    const callback = ({ response: _response, statusCode: _statusCode, error }) => {
      if (error) {
        setIsSubmitting(false);
        showToast('Something went wrong and your order wasn’t placed. Please try again.', 'error');
        handleClose();
        return;
      }

      setIsSubmitting(false);
      showToast(
        isPropertySkipTrace
          ? 'Skip Trace completed successfully.'
          : 'Skip Trace in progress. This may take a few minutes — we’ll email you when it’s ready.',
        'success'
      );

      handleClose();
    };

    dispatch(
      isPropertySkipTrace
        ? propertySkipTrace(
            {
              ...payload,
              contactId: contactIds?.[0],
              paymentMethodId,
              cardCode,
              paymentAmount,
              creditAmount,
            },
            // The API middleware calls afterSuccess with { getState, dispatch, response }, so the
            // skip trace status is on `response`, not on the argument itself.
            ({ response } = {}) => {
              if (response?.status && response.status !== 'SUCCESS') {
                showToast(response?.message || 'Skip Trace was not completed.', 'error');
                onFailure?.(response.status);
                handleClose();
                return;
              }

              showToast('Skip Trace completed successfully.', 'success');
              onSuccess?.();
              handleClose();
            },
            () => {
              showToast(
                'Something went wrong and your order wasn’t placed. Please try again.',
                'error'
              );
              onFailure?.('ERROR');
              handleClose();
            }
          )
        : appendContacts(
            {
              ...payload,
              contactIds,
            },
            paymentMethodId,
            cardCode,
            paymentAmount,
            creditAmount,
            callback
          )
    );
  };

  if (showConnectPromo) {
    const CONNECT_PROMO_BENEFITS = [
      { title: 'Free skip tracing included', subtitle: 'No per-lookup fees - ever' },
      {
        title: 'Access owner phone numbers instantly',
        subtitle: 'Find more contacts and connect faster.',
      },
      { title: 'Save on every contact you skip trace', subtitle: 'More skips, more savings.' },
    ];

    return (
      <div className={css.modalOverlay}>
        <div className={classNames(css.modalWrapper, css.connectPromoWrapper)}>
          <div
            ref={modalRef}
            tabIndex={-1}
            className={classNames(css.modalBox, css.connectPromoBox, 'modal-content')}
          >
            <button
              type="button"
              className={css.connectPromoCloseBtn}
              onClick={() => handleClose({ cancelled: true })}
              aria-label="Close"
            >
              <img src={connectPromoCloseIcon} alt="" aria-hidden="true" width="18" height="18" />
            </button>

            <div className={css.connectPromoBody}>
              <div className={css.connectPromoLeft}>
                <div
                  className={css.connectPromoBadge}
                  style={{ left: 0, top: 0, width: 301, height: 45 }}
                >
                  <img
                    src={connectPromoGiftIcon}
                    alt=""
                    aria-hidden="true"
                    width="24"
                    height="24"
                  />
                  <span className={css.connectPromoBadgeText}>INCLUDED WITH ADD-ON</span>
                </div>

                <h2
                  className={css.connectPromoHeadline}
                  style={{ left: 0, top: 65, width: 441, height: 54 }}
                >
                  Get Skip Tracing For <span className={css.connectPromoHeadlineAccent}>Free</span>
                </h2>

                <p
                  className={css.connectPromoSubtext}
                  style={{ left: 0, top: 139, width: 551, height: 90 }}
                >
                  Skip Tracing is included with the {getConnectLabel()} / Property &amp; Seller
                  Contacts add-on.
                  <br />
                  Stop paying per skip trace and start saving today.
                </p>

                <div
                  className={css.connectPromoDivider}
                  style={{ left: 0, top: 259, width: 551 }}
                />

                {CONNECT_PROMO_BENEFITS.map(({ title, subtitle }, index) => (
                  <div
                    className={css.connectPromoBenefitRow}
                    key={title}
                    style={{ left: 8, top: 289 + index * 86 }}
                  >
                    <span className={css.connectPromoBenefitIcon}>
                      <img
                        src={connectPromoCheckIcon}
                        alt=""
                        aria-hidden="true"
                        width="34"
                        height="34"
                      />
                    </span>
                    <span className={css.connectPromoBenefitText}>
                      <span className={css.connectPromoBenefitTitle}>{title}</span>
                      <span className={css.connectPromoBenefitSubtitle}>{subtitle}</span>
                    </span>
                  </div>
                ))}

                <div
                  className={css.connectPromoCallout}
                  style={{ left: 0, top: 565, width: 551, height: 93 }}
                >
                  <span className={css.connectPromoCalloutIcon}>
                    <img
                      src={connectPromoSavingsIcon}
                      alt=""
                      aria-hidden="true"
                      width="42"
                      height="54"
                    />
                  </span>
                  <span className={css.connectPromoCalloutText}>
                    <span className={css.connectPromoCalloutTitle}>
                      Stop paying per skip trace.
                    </span>
                    <span className={css.connectPromoCalloutSubtitle}>
                      Upgrade now and skip trace as much as you need.
                    </span>
                  </span>
                </div>
              </div>

              <div
                className={css.connectPromoActions}
                style={{ left: 56, top: 756, width: 525, height: 60 }}
              >
                <button
                  type="button"
                  className={css.connectPromoUpgradeBtn}
                  onClick={handleUpgradeToConnect}
                >
                  Upgrade
                </button>
                <button
                  type="button"
                  className={css.connectPromoContinueBtn}
                  onClick={handleContinuePaidSkipTrace}
                >
                  Continue with Paid Skip Tracing
                </button>
              </div>

              <div className={css.connectPromoRight}>
                <img
                  src={connectPromoIllustrationFull}
                  alt=""
                  aria-hidden="true"
                  width="594"
                  height="619"
                  className={css.connectPromoIllustration}
                />
              </div>
            </div>

            <CheckboxItem
              label="Don't show again today"
              checked={dontShowConnectPromoAgainToday}
              onChange={() => setDontShowConnectPromoAgainToday((v) => !v)}
              labelClassName={classNames(
                css.checkBoxLabelClassName,
                css.connectPromoSuppressCheckboxLabel
              )}
              containerClassName={css.connectPromoSuppressCheckbox}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={css.modalOverlay}>
      <div className={css.modalWrapper}>
        <div ref={modalRef} tabIndex={-1} className={classNames(css.modalBox, 'modal-content')}>
          <div className={css.modal_header}>{title}</div>

          <div className={css.modal_body}>
            {/* LEFT SIDE */}
            <div className={css.leftCol}>
              <div className={css.list_title}>Name Your List</div>

              <input
                type="text"
                value={listName}
                placeholder="Enter list name"
                className={css.input}
                onChange={(e) => setListName(e.target.value)}
              />

              <div className={css.checkbox_row}>
                <CheckboxItem
                  label="Re-Skip Trace"
                  checked={reSkipTrace}
                  onChange={() => handlereSkipTraceChange()}
                  labelClassName={css.checkBoxLabelClassName}
                />

                <div className={css.info_wrapper}>
                  <Tooltip
                    title="Re- Skip Trace"
                    description="Rerun previously skip-traced contacts in this list (fees apply). If unchecked, contacts traced in the last 6 months will be suppressed."
                    position="bottom"
                    bgColor="#128292"
                    width={312}
                    xOffset={125}
                    yOffset={-10}
                    arrowOffset={30}
                  >
                    <img src={infoGray} alt="info" className={css.infoIcon} />
                  </Tooltip>
                </div>
              </div>
              <div className={css.orderInfo}>
                Your card is authorized for all selected records, and refunded for any without
                contact info. By clicking “Place Order” you agree to the
                <a
                  target="_blank"
                  href="https://www.propstream.com/terms-of-use"
                  className={css.highlight}
                  rel="noreferrer"
                >
                  {' '}
                  PropStream Terms and Conditions
                </a>
                , and
                <span className={css.highlight} onClick={openDNCPolicyModal}>
                  {' '}
                  DNC Policy
                </span>
                .
              </div>
            </div>

            {/* RIGHT SIDE */}
            <div className={css.rightCol}>
              <div className={css.orderBox}>
                {loading && (
                  <div className={css.loadingOverlay}>
                    <SVG icon="spinner" className={classNames(css.mainspinner)} />
                  </div>
                )}
                <div className={css.orderTitle}>Order Details</div>
                <div className={css.orderDetails}>
                  <div className={css.orderRow}>
                    <span>Selected Contacts</span>
                    <span>{selectedProperties ? selectedProperties : contactIds?.length}</span>
                  </div>

                  <div className={css.orderRow}>
                    <span>
                      Eligible Contacts
                      <Tooltip
                        title="Eligible Contacts"
                        description="Some contacts will be omitted if they are duplicates, already Skip Traced, or missing information."
                        position="bottom"
                        bgColor="#128292"
                        width={312}
                        xOffset={125}
                        yOffset={2}
                        arrowOffset={30}
                      >
                        <img src={infoGray} alt="info" className={css.eligibleinfoIcon} />
                      </Tooltip>
                    </span>
                    <span>{appendJob?.newQuantity}</span>
                  </div>

                  <div className={css.orderRow}>
                    <span>Price Per Match</span>
                    <span>${Number(appendJob?.rate ?? 0).toFixed(2)}</span>
                  </div>

                  <div className={classNames(css.orderRow, css.subtotalRow)}>
                    <span>Subtotal</span>
                    <span>${Number(appendJob?.subtotal ?? 0).toFixed(2)}</span>
                  </div>

                  {listManagementEnabled ? (
                    <div className={classNames(css.orderRow)}>
                      <span>Free Skip Trace Credits</span>
                      <span>-${Number(appendJob?.freeSkipTraceCredit ?? 0).toFixed(2)}</span>
                    </div>
                  ) : null}

                  {!listManagementEnabled && prepaidInfo?.remainingPrepaidCredit ? (
                    <div className={classNames(css.orderRow)}>
                      <span>Prepaid Credits</span>
                      <span>-${Number(appendJob?.prepaidcredits ?? 0).toFixed(2)}</span>
                    </div>
                  ) : null}

                  <div className={classNames(css.orderRow, css.totalRow)}>
                    <span>Total</span>
                    <span>${Number(appendJob?.total ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {appendJob?.total > 0 && (
                <div className={css.paymentBox}>
                  <div className={css.paymentTitle}>Payment Method</div>

                  <NewCreditCardDropdown
                    tag={false}
                    options={cardList}
                    selectedCard={selectedCard}
                    onChange={handleCardChange}
                    AddPaymentMethod={AddPaymentMethod}
                  />

                  {appendJob?.total > 100 && (
                    <div className={css.cvvWrapper}>
                      <div className={css.cvvInputWrapper}>
                        <input
                          type={cvvVisible ? 'text' : 'password'}
                          maxLength={4}
                          placeholder="Enter CVV"
                          className={classNames(css.cvvInput, {
                            [css.cvvInputError]: cvvError,
                          })}
                          value={cvv}
                          onChange={handleCvvChange}
                          onBlur={() => setCvvError(validateCVV(cvv))}
                        />

                        <img
                          src={cvvVisible ? eyeOpen : eyeClosed}
                          alt="toggle visibility"
                          className={css.cvvToggle}
                          onClick={() => setCvvVisible((v) => !v)}
                        />
                      </div>

                      {cvvError && <div className={css.cvvErrorText}>{cvvError}</div>}
                      <div className={css.cvvNote}>
                        For your security, please re-enter the CVV for your saved card to complete
                        this purchase
                      </div>
                    </div>
                  )}

                  {selectedCard && isCardExpired(selectedCard?.expDate) && (
                    <div className={css.expCardMsg}>
                      Your card has expired. Please use another card.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className={css.modal_footer}>
            <button
              type="button"
              className={classNames(css.btn, css.btn_secondary)}
              onClick={() => handleClose({ cancelled: true })}
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!listName.trim() || isSubmitting}
              className={classNames(css.btn, css.btn_primary, {
                [css.disabledBtn]: !listName.trim() || isSubmitting,
              })}
              onClick={handlePlaceOrder}
            >
              {isSubmitting ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const registrationId = getPopupRegistration(SkipTraceModal);
SkipTraceModal.open = (props = {}) =>
  openPopup(registrationId, { ...props, priority: Priority.MEDIUM });
SkipTraceModal.close = () => closePopup({ popup: registrationId });

export default SkipTraceModal;
